import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, GitCompare, Plus } from "lucide-react";

export default function Compare() {
  const [all, setAll] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/careers/").then((r) => setAll(r.data)).finally(() => setLoading(false));
  }, []);

  const toggle = (id) => {
    setData(null);
    if (chosen.includes(id)) setChosen(chosen.filter((x) => x !== id));
    else if (chosen.length >= 4) toast.error("Compare up to 4 careers");
    else setChosen([...chosen, id]);
  };

  const runCompare = async () => {
    if (chosen.length < 2) { toast.error("Pick at least 2 careers"); return; }
    setBusy(true);
    try {
      const r = await api.post("/compare/", { career_ids: chosen });
      setData(r.data);
      api.post("/achievements/track", { action: "compare" }).catch(() => {});
    } catch (e) { toast.error("Compare failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <AppShell><div className="p-10 text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="compare-page">
        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">Compare</p>
        <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Side-by-side career comparison</h1>
        <p className="text-slate-400 mt-2 max-w-xl">Pick 2 to 4 careers and see them stacked — streams, exams, salary, entrance paths.</p>

        <div className="mt-8 brimi-card p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Choose careers ({chosen.length}/4)</p>
          <div className="mt-3 flex flex-wrap gap-2 max-h-[220px] overflow-y-auto pr-2">
            {all.map((c) => {
              const active = chosen.includes(c.id);
              return (
                <button key={c.id} data-testid={`compare-pick-${c.id}`} onClick={() => toggle(c.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${active ? "text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                  style={active ? { background: "#3B82F6", borderColor: "#3B82F6" } : {}}>
                  {active ? "✓ " : "+ "}{c.name}
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex justify-end">
            <button data-testid="compare-run" onClick={runCompare} disabled={busy || chosen.length < 2} className="brimi-pill accent-coral flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4" />} Compare
            </button>
          </div>
        </div>

        {data?.careers && (
          <div className="mt-8 overflow-x-auto" data-testid="compare-table">
            <table className="min-w-full bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.15em] text-slate-500 font-bold sticky left-0 bg-slate-50 z-10">Attribute</th>
                  {data.careers.map((c) => (
                    <th key={c.id} className="text-left px-4 py-3 min-w-[220px]">
                      <p className="font-display text-lg text-slate-900">{c.name}</p>
                      <p className="text-[11px] text-slate-500">{c.category}</p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-sm text-slate-800">
                <Row label="Tagline" careers={data.careers} render={(c) => c.tagline} />
                <Row label="Streams" careers={data.careers} render={(c) => (c.streams || []).join(", ")} />
                <Row label="Key subjects" careers={data.careers} render={(c) => (c.key_subjects || []).join(", ")} />
                <Row label="Entrance (India)" careers={data.careers} render={(c) => (c.entrance_exams_india || []).join(", ")} />
                <Row label="Entrance (Global)" careers={data.careers} render={(c) => (c.entrance_exams_global || []).join(", ")} />
                <Row label="Avg salary (India)" careers={data.careers} render={(c) => c.avg_salary_india} />
                <Row label="Avg salary (Global)" careers={data.careers} render={(c) => c.avg_salary_global} />
                <Row label="Top roles" careers={data.careers} render={(c) => (c.top_roles || []).join(", ")} />
                <Row label="Why it grows" careers={data.careers} render={(c) => c.why} />
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Row({ label, careers, render }) {
  return (
    <tr className="border-t border-slate-100">
      <td className="px-4 py-3 font-bold text-slate-500 uppercase tracking-[0.1em] text-[11px] sticky left-0 bg-white z-10">{label}</td>
      {careers.map((c) => (
        <td key={c.id} className="px-4 py-3 align-top">{render(c) || "—"}</td>
      ))}
    </tr>
  );
}
